import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface StockedAt {
  branchId: string;
  branchName?: string;
  quantity: number;
}

interface Product {
  id: string;
  name: string;
  category: string;
  price: string;
  zeroRated: boolean;
  lowThreshold: number;
  active: boolean;
  stockedAt: StockedAt[];
}

interface Shop {
  id: string;
  name: string;
  location: string;
  active: boolean;
  staffCount: number;
  productCount: number;
}

/**
 * Adding shops and products, and changing what things cost.
 *
 * Every price here is the shelf price — what the customer hands over. VAT is
 * already inside it, so type in exactly what is on the label.
 */
export default function CataloguePanel({ activeBranchId }: { activeBranchId: string }) {
  const [tab, setTab] = useState<'products' | 'shops'>('products');
  const [products, setProducts] = useState<Product[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // new product
  const [pName, setPName] = useState('');
  const [pCategory, setPCategory] = useState('');
  const [pPrice, setPPrice] = useState('');
  const [pZero, setPZero] = useState(false);

  // new shop
  const [sName, setSName] = useState('');
  const [sLocation, setSLocation] = useState('');

  // inline price editing
  const [editing, setEditing] = useState<string | null>(null);
  const [editPrice, setEditPrice] = useState('');

  const flash = (m: string) => {
    setMsg(m);
    setErr('');
    setTimeout(() => setMsg(''), 4000);
  };
  const fail = (e: any, fallback: string) => {
    const d = e?.response?.data?.message;
    setErr(Array.isArray(d) ? d.join(', ') : d || fallback);
    setMsg('');
  };

  const load = async () => {
    try {
      const [p, s] = await Promise.all([
        apiClient.listProducts(showInactive),
        apiClient.listShops(showInactive),
      ]);
      setProducts(Array.isArray(p.data) ? p.data : []);
      setShops(Array.isArray(s.data) ? s.data : []);
    } catch (e: any) {
      fail(e, 'Could not load the catalogue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [showInactive]);

  const addProduct = async () => {
    if (!pName.trim() || !pPrice.trim()) {
      setErr('A product needs a name and a price.');
      return;
    }
    setBusy(true);
    try {
      const res = await apiClient.createProduct(
        pName.trim(),
        pCategory.trim() || 'Other',
        Number(pPrice).toFixed(2),
        undefined,
        undefined,
        pZero,
      );
      flash(res.data.message);
      setPName('');
      setPCategory('');
      setPPrice('');
      setPZero(false);
      await load();
    } catch (e: any) {
      fail(e, 'Could not add the product');
    } finally {
      setBusy(false);
    }
  };

  const savePrice = async (p: Product) => {
    if (!editPrice.trim()) return;
    setBusy(true);
    try {
      const res = await apiClient.updateProduct(p.id, { price: Number(editPrice).toFixed(2) });
      flash(res.data.message);
      setEditing(null);
      await load();
    } catch (e: any) {
      fail(e, 'Could not change the price');
    } finally {
      setBusy(false);
    }
  };

  const toggleZeroRated = async (p: Product) => {
    setBusy(true);
    try {
      await apiClient.updateProduct(p.id, { zeroRated: !p.zeroRated });
      flash(`${p.name} is now ${p.zeroRated ? 'standard-rated at 15%' : 'zero-rated'}.`);
      await load();
    } catch (e: any) {
      fail(e, 'Could not change that');
    } finally {
      setBusy(false);
    }
  };

  const toggleProduct = async (p: Product) => {
    setBusy(true);
    try {
      await apiClient.updateProduct(p.id, { active: !p.active });
      flash(`${p.name} ${p.active ? 'taken off sale' : 'put back on sale'}.`);
      await load();
    } catch (e: any) {
      fail(e, 'Could not change that');
    } finally {
      setBusy(false);
    }
  };

  const stockHere = async (p: Product) => {
    setBusy(true);
    try {
      const res = await apiClient.stockProductAt(p.id, activeBranchId, 0);
      flash(res.data.message);
      await load();
    } catch (e: any) {
      fail(e, 'Could not stock it here');
    } finally {
      setBusy(false);
    }
  };

  const addShop = async () => {
    if (!sName.trim() || !sLocation.trim()) {
      setErr('A shop needs a name and a location.');
      return;
    }
    setBusy(true);
    try {
      const res = await apiClient.createShop(sName.trim(), sLocation.trim());
      flash(res.data.message);
      setSName('');
      setSLocation('');
      await load();
    } catch (e: any) {
      fail(e, 'Could not add the shop');
    } finally {
      setBusy(false);
    }
  };

  const toggleShop = async (s: Shop) => {
    setBusy(true);
    try {
      await apiClient.updateShop(s.id, { active: !s.active });
      flash(`${s.name} ${s.active ? 'closed' : 'reopened'}.`);
      await load();
    } catch (e: any) {
      fail(e, 'Could not change that');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-gray-500 py-8">Loading…</div>;

  const field =
    'w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent';

  return (
    <div className="space-y-6">
      {msg && <div className="bg-green-50 text-green-800 rounded-lg px-4 py-3 text-sm font-medium">{msg}</div>}
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-2">
          {(['products', 'shops'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize ${
                tab === t ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show ones not on sale
        </label>
      </div>

      {tab === 'products' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6 h-fit">
            <h3 className="text-lg font-medium text-gray-900">Add a product</h3>
            <p className="text-sm text-gray-500 mt-1">
              Type the shelf price. VAT is already in it.
            </p>
            <div className="space-y-3 mt-5">
              <input value={pName} onChange={(e) => setPName(e.target.value)} placeholder="Name, e.g. Seed Loaf" className={field} />
              <input
                value={pCategory}
                onChange={(e) => setPCategory(e.target.value)}
                placeholder="Category, e.g. Loaves"
                list="mepco-categories"
                className={field}
              />
              <datalist id="mepco-categories">
                {Array.from(new Set(products.map((p) => p.category))).map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                <input
                  value={pPrice}
                  onChange={(e) => setPPrice(e.target.value)}
                  placeholder="0.00"
                  inputMode="decimal"
                  className={`${field} pl-8 tabular-nums`}
                />
              </div>
              <label className="flex items-start gap-2.5 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pZero}
                  onChange={(e) => setPZero(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Zero-rated for VAT
                  <span className="block text-xs text-gray-500">
                    Brown bread, maize meal, milk, eggs. Same shelf price, but none of it is VAT.
                  </span>
                </span>
              </label>
              <button
                onClick={addProduct}
                disabled={busy}
                className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold py-3 rounded-lg"
              >
                Add product
              </button>
              <p className="text-xs text-gray-500">
                It goes on the shelf at every shop at zero stock. Count it in from Inventory.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow lg:col-span-2 overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">The range</h3>
              <p className="text-sm text-gray-500">{products.length} products · tap a price to change it</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {products.map((p) => {
                    const here = p.stockedAt.find((s) => s.branchId === activeBranchId);
                    return (
                      <tr key={p.id} className={`border-b last:border-0 ${p.active ? '' : 'opacity-55'}`}>
                        <td className="py-3 px-6">
                          <p className="font-medium text-gray-900">{p.name}</p>
                          <p className="text-xs text-gray-500">
                            {p.category}
                            {!p.active && ' · not on sale'}
                          </p>
                          <button
                            onClick={() => toggleZeroRated(p)}
                            disabled={busy}
                            className={`mt-1 text-[11px] px-1.5 py-0.5 rounded font-medium ${
                              p.zeroRated
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                            title="Zero-rated goods are sold at the same price but carry no VAT"
                          >
                            {p.zeroRated ? 'zero-rated' : 'VAT 15%'}
                          </button>
                        </td>
                        <td className="py-3 px-4 w-36">
                          {editing === p.id ? (
                            <div className="flex gap-1">
                              <input
                                value={editPrice}
                                onChange={(e) => setEditPrice(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') savePrice(p);
                                  if (e.key === 'Escape') setEditing(null);
                                }}
                                autoFocus
                                inputMode="decimal"
                                className="w-20 px-2 py-1 border border-amber-400 rounded tabular-nums text-right"
                              />
                              <button onClick={() => savePrice(p)} className="text-amber-600 font-bold px-1">
                                ✓
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setEditing(p.id);
                                setEditPrice(p.price);
                              }}
                              className="tabular-nums font-semibold text-gray-900 hover:text-amber-600 hover:underline"
                            >
                              R {Number(p.price).toFixed(2)}
                            </button>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right tabular-nums text-gray-600 w-28">
                          {here ? (
                            `${here.quantity} here`
                          ) : (
                            <button onClick={() => stockHere(p)} className="text-xs text-amber-600 hover:underline">
                              stock it here
                            </button>
                          )}
                        </td>
                        <td className="py-3 px-6 text-right w-24">
                          <button
                            onClick={() => toggleProduct(p)}
                            disabled={busy}
                            className="text-xs text-gray-500 hover:text-gray-900 hover:underline"
                          >
                            {p.active ? 'Take off sale' : 'Put on sale'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6 h-fit">
            <h3 className="text-lg font-medium text-gray-900">Open a shop</h3>
            <p className="text-sm text-gray-500 mt-1">It starts with the full range at zero stock.</p>
            <div className="space-y-3 mt-5">
              <input value={sName} onChange={(e) => setSName(e.target.value)} placeholder="Name, e.g. The Corner Bakery" className={field} />
              <input value={sLocation} onChange={(e) => setSLocation(e.target.value)} placeholder="Where it is" className={field} />
              <button
                onClick={addShop}
                disabled={busy}
                className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold py-3 rounded-lg"
              >
                Open shop
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow lg:col-span-2 overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">Your shops</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {shops.map((s) => (
                    <tr key={s.id} className={`border-b last:border-0 ${s.active ? '' : 'opacity-55'}`}>
                      <td className="py-4 px-6">
                        <p className="font-medium text-gray-900">{s.name}</p>
                        <p className="text-xs text-gray-500">
                          {s.location}
                          {!s.active && ' · closed'}
                        </p>
                      </td>
                      <td className="py-4 px-4 text-gray-600 tabular-nums text-right">{s.staffCount} staff</td>
                      <td className="py-4 px-4 text-gray-600 tabular-nums text-right">{s.productCount} products</td>
                      <td className="py-4 px-6 text-right">
                        <button
                          onClick={() => toggleShop(s)}
                          disabled={busy}
                          className="text-xs text-gray-500 hover:text-gray-900 hover:underline"
                        >
                          {s.active ? 'Close' : 'Reopen'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
